import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import discountCodeContext from "../../../context/discount-code/context";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import { IDiscountCode } from "../../../models/discount-code/response";
import {
    getDiscountStatus,
    getDiscountStatusColor,
    getDiscountType,
    getDiscounteTypeColor, getTypeInvitationDiscountCode,
} from "../../../models/discount-code/enum";
import Tag from "../../general/antd/tag";
import moment from "moment";
import { useModal } from "react-modal-hook";
import FormHookModal from "../../general/modals/form-hook-modal";
import SendNotificationMessage from "../discount-code-send-notification";
import { Button, Tooltip } from "antd";
import { AiOutlineNotification } from "react-icons/ai";
import { ImCheckmark, ImCross } from "react-icons/im";
import AuthContext from "../../../context/auth/context";

interface IProps { }

const DiscountCodeTable: React.FC<IProps> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { list, actions, loading, query } = useContext(discountCodeContext);

    const [passedId, setPassedId] = useState<number>();
    const [message, setMessage] = useState<string>();
    const { userPermissions: permissions } = useContext(AuthContext);

    let userPermissions =
        permissions?.length > 0
            ? permissions
            : localStorage?.getItem("permisionStoroge")?.split(",");
    if (!userPermissions) userPermissions = [];
    const [showFormModal, hideFormModal] = useModal(
        () => (
            <FormHookModal
                id="3"
                confirmButtonText={t("send")}
                loading={loading.includes("send")}
                title={t("send_notification")}
                hideModal={() => {
                    hideFormModal();
                }}
                width={"50vw"}
                style={{
                    marginTop: "1.6rem",
                    height: "59vh",
                }}
                onSubmit={async (data) => {
                    await actions.sendNotificationDiscountCodeClient(Number(passedId), data);
                }}
                defaultValues={{
                    message: message
                }}
            >
                <SendNotificationMessage></SendNotificationMessage>
            </FormHookModal>
        ),
        [loading, passedId, message]
    );


    const columns: ColumnProps<IDiscountCode>[] = [
        {
            title: t("id"),
            dataIndex: "id",
            key: "id",
            sorter: {
                multiple: 1,
            },
            width: 100,
            align: "center",
        },
        {
            title: t("name"),
            dataIndex: "name",
            key: "name",
            width: 100,
            align: "center",
        },

        {
            title: t("code"),
            dataIndex: "code",
            align: "center",
            key: "code",
            sorter: {
                multiple: 2,
            },
        },

        {
            title: t("type"),
            dataIndex: "type_discount",
            align: "center",
            key: "type",
            render: (_, record) => {
                return (
                    <Tag
                        title={getDiscountType(record?.type)}
                        color={getDiscounteTypeColor(record?.type)}
                    />
                );
            },
        },
        {
            title: t("by_invitation"),
            dataIndex: "isInvitation",
            align: "center",
            key: "isInvitation",
            minWidth: 180,
            render: (_, record) => {
                return (
                    <>
                        {record?.isInvitation ? (
                            <ImCheckmark color="green" />
                        ) : (
                            <ImCross color="red" />
                        )}
                    </>
                );
            },
        },
        {
            title: t("invitation_type"),
            dataIndex: "invitationType",
            align: "center",
            key: "invitationType",
            render: (_, record) => {
                return (
                    <Tag
                        title={getTypeInvitationDiscountCode(record?.invitationType)}
                        color={getDiscounteTypeColor(record?.invitationType)}
                    />
                );
            },
        },
        {
            title: t("validity_date"),
            dataIndex: "validityDate",
            align: "center",
            key: "validityDate",
            minWidth: 140,
            render: (_, record) => {
                return record?.validityDate
                    ? moment(record?.validityDate).format("YYYY-MM-DD")
                    : "-";
            },
        },
        {
            title: t("status_discount"),
            dataIndex: "status_discount",
            align: "center",
            key: "status",
            render: (_, record) => {
                return (
                    <Tag
                        title={getDiscountStatus(record?.status)}
                        color={getDiscountStatusColor(record?.status)}
                    />
                );
            },
        },

        {
            title: t("code_validity"),
            dataIndex: "codeValidity",
            align: "center",
            key: "codeValidity",
            minWidth: 180,
            render: (_, record) => {
                return record?.codeValidity ?? "-";
            },
        },
        {
            title: t("number_points"),
            dataIndex: "numberPoints",
            align: "center",
            // width: "15%",
            minWidth: 130,
            key: "numberPoints",
            render: (_, record) => {
                return record?.numberPoints ?? "-";
            },
        },
        {
            title: t("frequency_of_use"),
            dataIndex: "maxOfUse",
            align: "center",
            key: "maxOfUse",
            minWidth: 130,
            render: (_, record) => {
                return record?.maxOfUse ?? "-";
            },
        },
        {
            title: t("minimum_limit"),
            dataIndex: "minimumLimit",
            align: "center",
            key: "minimumLimit",
            render: (_, record) => {
                return record?.minimumLimit ?? "-";
            },
        },
        {
            title: t("upper_limit"),
            dataIndex: "upperLimit",
            align: "center",
            key: "upperLimit",
            render: (_, record) => {
                return record?.upperLimit ?? "-";
            },
        },
        {
            title: t("overall_upper_limit"),
            dataIndex: "valueOfAllDiscountCodes",
            align: "center",
            key: "valueOfAllDiscountCodes",
            render: (_, record) => {
                return record?.valueOfAllDiscountCodes ?? "-";
            },
        },
        {
            title: t("actions"),
            width: 160,
            dataIndex: "",
            align: "center",
            fixed: "right",
            key: "x",
            render: (_: any, record) => (
                <Space>
                    <ViewBtn
                        onClick={(e) => {
                            const url = `/discount-codes/details/${record?.id}`;
                            if (e.ctrlKey || e.metaKey) {
                                window.open(url, "_blank");
                            } else {
                                navigate(url);
                            }
                        }}
                    />
                    {/*<EditBtn*/}
                    {/*  onClick={async () => {*/}
                    {/*    navigate(`update/${record?.id}`);*/}
                    {/*  }}*/}
                    {/*/>*/}
                    {userPermissions.includes('can_create_discount_code') && <Tooltip title={t('send')}>
                        <Button shape='circle' disabled={record?.isInvitation} icon={<AiOutlineNotification />} onClick={() => {
                            setPassedId(record?.id)
                            setMessage(record?.message)
                            showFormModal()
                        }} />
                    </Tooltip>}
                    {/*<DeleteBtn*/}
                    {/*    onConfirm={async () => {*/}
                    {/*        await actions.deleteDiscountCode(record?.id);*/}
                    {/*        eventManager.emit(EVENT_SUCCESS);*/}
                    {/*    }}*/}
                    {/*    loading={loading.includes("delete")}*/}
                    {/*/>*/}
                </Space>
            ),
        },
    ];

    let tableFiltersProps = {};
    const [sorts, setSorts] = useState<ISort[]>([]);
    const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

    useEffect(() => {
        sorts.length > 0 &&
            actions.setQuery({
                ...query,
                orders: sorts,
            });
    }, [sorts]);

    return (
        <>
            <Table<any>
                rowKey="id"
                showPagination={true}
                columns={columns}
                dataSource={list?.data ?? []}
                loading={loading.includes("list") || list == undefined}
                size="small"
                total={list?.total}
                pageSize={query.perPage}
                page={query.page}
                onPaginationChange={(page, pageSize) => {
                    actions.setQuery({
                        ...query,
                        page,
                        perPage: pageSize,
                    });
                }}
                // scroll={{ x: 1400 }}
                onChange={(_, tableFilters, tabelSorters) => {
                    tableOnChange(
                        tableFilters,
                        tabelSorters,
                        tableFiltersProps,
                        setSorts,
                        setStaticFilters
                    );
                }}
            />
        </>
    );
};

export default DiscountCodeTable;
